import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { FormControl, FormLabel, Input, makeStyles } from '@material-ui/core'
import { setToolOptions } from 'store/tool/actions'
import { getToolOptions, getSelectedMaterials, getMaterials } from 'store/selectors'
import { updateShapeData } from 'store/materials/actions'

const useStyles = makeStyles({
  root: {
    padding: '15px',
  },
  formControl: {
    width: '100%',
    marginBottom: '15px',
  },
  label: {
    marginBottom: '5px',
  },
})

interface StateProps {
  options: {
    radius: number
  }
  selectedMaterials: (string | number)[]
  materials: { [key: string]: ReduxStore.Materials.Data.IMaterial }
}

interface DispatchProps {
  dispatchSetToolOptions: (options: any) => void
  dispatchUpdateShapeData: (newShape: ReduxStore.Materials.Data.IOverlay) => void
}

type Props = StateProps & DispatchProps

const PointToolOptions: React.FC<Props> = ({ 
  options, 
  dispatchSetToolOptions, 
  selectedMaterials,
  materials,
  dispatchUpdateShapeData 
}) => {
  const classes = useStyles()
  const reduxRadius = (options && options.radius) ? options.radius : 25
  
  // Local state to handle the input value, allowing empty string
  const [inputValue, setInputValue] = useState(reduxRadius.toString())

  // Update local state when Redux state changes
  useEffect(() => {
    setInputValue(reduxRadius.toString())
  }, [reduxRadius])

  const handleRadiusChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value
    
    // Always update the local state to allow empty input
    setInputValue(value)
    
    // Only update Redux if we have a valid number
    const numValue = Number(value)
    if (value !== '' && !isNaN(numValue) && numValue >= 1 && numValue <= 100) {
      // Update tool options for new points
      dispatchSetToolOptions({
        radius: numValue,
      })
      
      // Update radius for selected points
      selectedMaterials.forEach(materialId => {
        const material = materials[materialId]
        if (material && material.shape === 'point') {
          const shapeData = JSON.parse(material.shape_data)
          shapeData.radius = numValue
          dispatchUpdateShapeData({
            overlay_id: material.overlay_id,
            material_id: material.material_id,
            drawing_id: material.drawing_id,
            shape_data: JSON.stringify(shapeData),
            shape: material.shape
          })
        }
      })
    }
  }
  
  const handleBlur = () => {
    // On blur, if the input is empty or invalid, reset to the Redux value
    const numValue = Number(inputValue)
    if (inputValue === '' || isNaN(numValue) || numValue < 1 || numValue > 100) {
      setInputValue(reduxRadius.toString())
    }
  }

  // Update radius display when selection changes
  useEffect(() => {
    // If a single point is selected, show its radius
    if (selectedMaterials.length === 1) {
      const material = materials[selectedMaterials[0]]
      if (material && material.shape === 'point') {
        const shapeData = JSON.parse(material.shape_data)
        if (shapeData.radius && shapeData.radius !== reduxRadius) {
          dispatchSetToolOptions({
            radius: shapeData.radius,
          })
        }
      }
    }
  }, [selectedMaterials, materials, reduxRadius, dispatchSetToolOptions])

  return (
    <div className={classes.root}>
      <FormControl className={classes.formControl}>
        <FormLabel className={classes.label}>Point Radius</FormLabel>
        <Input
          type="number"
          value={inputValue}
          onChange={handleRadiusChange}
          onBlur={handleBlur}
          inputProps={{ min: 1, max: 100 }}
        />
      </FormControl>
    </div>
  )
}

const mapStateToProps = (state: ReduxStore.State) => {
  const options = getToolOptions(state, 'point') || {};
  return {
    options: { radius: (options as any).radius || 25 },
    selectedMaterials: getSelectedMaterials(state),
    materials: getMaterials(state)
  };
}

const mapDispatchToProps = {
  dispatchSetToolOptions: (options: any) => setToolOptions('point', options),
  dispatchUpdateShapeData: updateShapeData
}

export default connect(mapStateToProps, mapDispatchToProps)(PointToolOptions)